import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Download,
  ImageIcon,
  Italic,
  Paperclip,
  Type,
} from "lucide-react";
import {
  fetchCertificateTemplates,
  createCertificateTemplate,
  updateCertificateTemplate,
  deleteCertificateTemplate,
  downloadCertificateTemplateImage,
} from "../api/certificateTemplatesApi";
import { getCsrfToken } from "../../../public/api/authApi";
import { compressPhoto } from "../../../utils/compressImage";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
  GlobalTextAreaField,
} from "../../../global/forms/GlobalFormFields";
import CertificateLivePreview from "../components/CertificateLivePreview";
import {
  CERT_DATE_ALIGNMENTS,
  CERT_NAME_FONT_FAMILIES,
  CERT_PREVIEW_SAMPLE_NAME,
  CERT_TEMPLATE_DEFAULTS,
  normalizeFontFamilyId,
} from "../constants/certificateTemplateOptions";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalDataTable from "../../../global/GlobalDataTable";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../../user-builder/styles/userBuilderTable.css";
import "../styles/CertificateTemplates.css";

const FONT_OPTIONS = CERT_NAME_FONT_FAMILIES.map((f) => ({
  value: f.id,
  label: f.label,
}));

const DATE_ALIGN_OPTIONS = CERT_DATE_ALIGNMENTS.map((d) => ({
  id: d.id,
  label: d.label,
  Icon: d.id === "left" ? AlignLeft : d.id === "right" ? AlignRight : AlignCenter,
}));

const EMPTY_FORM = {
  id: null,
  purpose_name: "",
  matter: "",
  name_font_family: CERT_TEMPLATE_DEFAULTS.name_font_family,
  name_italic: CERT_TEMPLATE_DEFAULTS.name_italic,
  date_align: CERT_TEMPLATE_DEFAULTS.date_align,
};

function toBooleanItalic(value) {
  return value === true || value === 1 || value === "1";
}

function slugFromName(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isActiveTemplate(row) {
  return Number(row?.is_active) === 1;
}

function TemplateAttachmentCell({ templateId, onError }) {
  const [downloading, setDownloading] = useState(false);
  if (!templateId) return "—";

  const onDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadCertificateTemplateImage(templateId);
    } catch {
      onError?.("Could not download template image.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onDownload}
      disabled={downloading}
      title="Download template image"
      className="maintenance-directory-attachment-link cert-templates-attachment-link cert-templates-attachment-btn"
      aria-label="Download template image"
    >
      <Paperclip size={16} aria-hidden />
      <span className="cert-templates-attachment-label">
        {downloading ? "Downloading…" : "Download"}
      </span>
      <Download size={12} aria-hidden className="cert-templates-attachment-external" />
    </button>
  );
}

export default function CertificateTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [templateFile, setTemplateFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tablePreviewRow, setTablePreviewRow] = useState(null);
  const formSectionRef = useRef(null);
  const previewPanelRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await fetchCertificateTemplates();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.filter(isActiveTemplate));
    } catch (err) {
      setLoadError(err.message || "Failed to load certificate templates");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const setFormField = (name, value) => {
    setTablePreviewRow(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setFormError("");
  };

  const openTablePreview = (row) => {
    setTablePreviewRow(row);
    previewPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const previewImageUrl = tablePreviewRow
    ? tablePreviewRow.template_image_url
    : previewUrl;
  const previewPurpose = tablePreviewRow ? tablePreviewRow.purpose_name : formData.purpose_name;
  const previewMatter = tablePreviewRow ? tablePreviewRow.matter : formData.matter;
  const previewNameFont = normalizeFontFamilyId(
    tablePreviewRow ? tablePreviewRow.name_font_family : formData.name_font_family,
  );
  const previewNameItalic = tablePreviewRow
    ? toBooleanItalic(tablePreviewRow.name_italic)
    : Boolean(formData.name_italic);
  const previewDateAlign = tablePreviewRow ? tablePreviewRow.date_align : formData.date_align;

  const onFileChange = async (e) => {
    setTablePreviewRow(null);
    const file = e.target.files?.[0] || null;
    setFieldErrors((prev) => ({ ...prev, template_image: "" }));
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    if (!file) {
      setTemplateFile(null);
      setPreviewUrl(formData.existing_image_url || "");
      return;
    }
    try {
      const compressed = await compressPhoto(file);
      setTemplateFile(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    } catch {
      setTemplateFile(null);
      e.target.value = "";
      setFieldErrors((prev) => ({ ...prev, template_image: "Could not compress image" }));
      setPreviewUrl(formData.existing_image_url || "");
    }
  };

  const clearTemplateFile = () => {
    setTemplateFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(formData.existing_image_url || "");
    setFieldErrors((prev) => ({ ...prev, template_image: "" }));
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setTemplateFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPreviewUrl("");
    setTablePreviewRow(null);
    setIsEditing(false);
    setFieldErrors({});
    setFormError("");
  };

  const startEdit = (row) => {
    setTablePreviewRow(null);
    setFormData({
      id: row.id,
      purpose_name: row.purpose_name || "",
      matter: row.matter || "",
      name_font_family: normalizeFontFamilyId(
        row.name_font_family || CERT_TEMPLATE_DEFAULTS.name_font_family,
      ),
      name_italic: toBooleanItalic(row.name_italic),
      date_align: row.date_align || CERT_TEMPLATE_DEFAULTS.date_align,
      existing_image_url: row.template_image_url || "",
    });
    setTemplateFile(null);
    setPreviewUrl(row.template_image_url || "");
    setIsEditing(true);
    setFieldErrors({});
    setFormError("");
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const applyApiErrors = (err) => {
    const details = err?.details;
    if (details && typeof details === "object") {
      setFieldErrors(details);
    }
    setFormError(err?.message || "Save failed");
  };

  const handleSubmit = async () => {
    setFormError("");
    setFieldErrors({});
    setSubmitting(true);
    try {
      let csrfToken =
        localStorage.getItem("csrf_token") || localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        if (csrfToken) localStorage.setItem("csrf_token", csrfToken);
      }

      const fields = {
        purpose_name: formData.purpose_name.trim(),
        purpose_slug: slugFromName(formData.purpose_name),
        matter: formData.matter.trim(),
        name_font_family: normalizeFontFamilyId(
          formData.name_font_family || CERT_TEMPLATE_DEFAULTS.name_font_family,
        ),
        name_italic: Boolean(formData.name_italic),
        date_align: formData.date_align || CERT_TEMPLATE_DEFAULTS.date_align,
      };

      if (isEditing) {
        if (!templateFile && !formData.existing_image_url) {
          setFieldErrors({ template_image: "Template image is required" });
          setFormError("Please correct the highlighted field.");
          return;
        }
        await updateCertificateTemplate(formData.id, fields, templateFile, csrfToken);
        setSuccessMsg("Certificate template updated.");
      } else {
        if (!templateFile) {
          setFieldErrors({ template_image: "Template image is required" });
          setFormError("Please correct the highlighted field.");
          return;
        }
        await createCertificateTemplate(fields, templateFile, csrfToken);
        setSuccessMsg("Certificate template created.");
      }
      resetForm();
      await loadRows();
    } catch (err) {
      applyApiErrors(err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;
    setIsDeleting(true);
    try {
      let csrfToken =
        localStorage.getItem("csrf_token") || localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        if (csrfToken) localStorage.setItem("csrf_token", csrfToken);
      }
      await deleteCertificateTemplate(itemToDelete.id, csrfToken);
      setSuccessMsg("Certificate template deactivated.");
      setShowDelete(false);
      setItemToDelete(null);
      if (formData.id === itemToDelete.id) resetForm();
      await loadRows();
    } catch (err) {
      setFormError(err.message || "Deactivate failed");
      setShowDelete(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="user-builder-shell">
        <p className="ub-loading">Loading certificate templates…</p>
      </div>
    );
  }

  return (
    <div className="user-builder-shell cert-templates-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />

      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Certificate templates &amp; purposes</h2>
        </div>
      </header>

      <div className="cert-templates-layout">
          <div ref={formSectionRef} className="cert-templates-editor" style={{ scrollMarginTop: "1rem" }}>
          <div className="cert-templates-editor__form user-builder-panel">
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">
                {isEditing ? "Edit template" : "Add template"}
              </h3>
            </div>

            <GlobalFormShell
              legend={isEditing ? "Edit certificate template" : "New certificate template"}
              showAlert={!!formError}
              alertText={formError}
              gridClassName="cert-templates-form-grid"
              actions={
                <>
                  {isEditing ? (
                    <GlobalButton type="button" variant="ghost" onClick={resetForm}>
                      Cancel edit
                    </GlobalButton>
                  ) : null}
                  <GlobalButton
                    type="button"
                    variant="primary"
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? "Saving…" : isEditing ? "Save changes" : "Save template"}
                  </GlobalButton>
                </>
              }
            >
                <div className="cert-templates-form-grid__full">
                  <GlobalTextField
                    id="ct-purpose-name"
                    label="Certificate purpose"
                    value={formData.purpose_name}
                    onChange={(v) => setFormField("purpose_name", v)}
                    error={fieldErrors.purpose_name}
                    placeholder="e.g. Tournament participation"
                    required
                  />
                </div>
                {fieldErrors.purpose_slug ? (
                  <p className="gff-error cert-templates-form-grid__full" role="alert">
                    {fieldErrors.purpose_slug}
                  </p>
                ) : null}
                <div className="cert-templates-form-grid__full cert-templates-style-panel">
                  <p className="cert-templates-style-panel__title">Recipient name &amp; date</p>

                  <div className="cert-templates-style-row">
                    <div className="cert-templates-style-row__font">
                      <GlobalSelectField
                        id="ct-name-font"
                        label={
                          <span className="cert-templates-field-label">
                            <Type size={16} aria-hidden />
                            Name font
                          </span>
                        }
                        value={normalizeFontFamilyId(formData.name_font_family)}
                        onChange={(v) => setFormField("name_font_family", v)}
                        error={fieldErrors.name_font_family}
                        options={FONT_OPTIONS}
                        placeholderOption=""
                      />
                    </div>
                    <label
                      className={`cert-templates-italic-toggle${
                        formData.name_italic ? " is-on" : ""
                      }`}
                    >
                      <input
                        id="ct-name-italic"
                        type="checkbox"
                        className="cert-templates-italic-toggle__input"
                        checked={Boolean(formData.name_italic)}
                        onChange={(e) => setFormField("name_italic", e.target.checked)}
                      />
                      <Italic size={16} aria-hidden />
                      <span>Italic</span>
                    </label>
                  </div>
                  {fieldErrors.name_italic ? (
                    <p className="gff-error cert-templates-style-panel__error" role="alert">
                      {fieldErrors.name_italic}
                    </p>
                  ) : null}

                  <div className="cert-templates-style-date">
                    <span className="gff-label cert-templates-style-date__label">
                      Date position
                    </span>
                    <div
                      className="cert-templates-segment"
                      role="radiogroup"
                      aria-label="Date position"
                    >
                      {DATE_ALIGN_OPTIONS.map(({ id, label, Icon }) => {
                        const active = formData.date_align === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            className={`cert-templates-segment__btn${
                              active ? " is-active" : ""
                            }`}
                            aria-pressed={active}
                            onClick={() => setFormField("date_align", id)}
                          >
                            <Icon size={15} aria-hidden />
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {fieldErrors.date_align ? (
                      <p className="gff-error cert-templates-style-panel__error" role="alert">
                        {fieldErrors.date_align}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="cert-templates-form-grid__full">
                  <GlobalTextAreaField
                    id="ct-matter"
                    label="Matter (body text)"
                    value={formData.matter}
                    onChange={(v) => setFormField("matter", v)}
                    error={fieldErrors.matter}
                    placeholder="Certificate wording shown on the image…"
                    rows={5}
                    required
                  />
                </div>
                <div
                  className={`gff-field gff-field--full cert-templates-upload${
                    fieldErrors.template_image ? " cert-templates-upload--error" : ""
                  }`}
                >
                  <span className="gff-label">
                    Template image
                    <span className="cert-templates-upload__hint">
                      {isEditing
                        ? "Required — leave empty to keep the current image"
                        : "Required"}
                    </span>
                  </span>
                  <div className="cert-templates-upload__panel">
                    <div className="cert-templates-upload__row">
                      <input
                        ref={fileInputRef}
                        id="cert-template-image"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={onFileChange}
                        className="cert-templates-upload__native"
                        aria-label="Choose template image"
                      />
                      <GlobalButton
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={submitting}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon size={16} aria-hidden />
                        Choose image
                      </GlobalButton>
                      {templateFile ? (
                        <>
                          <span
                            className="cert-templates-upload__filename"
                            title={templateFile.name}
                          >
                            {templateFile.name}
                          </span>
                          <GlobalButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={submitting}
                            onClick={clearTemplateFile}
                          >
                            Remove
                          </GlobalButton>
                        </>
                      ) : (
                        <span className="cert-templates-upload__empty">
                          PNG, JPG, or WebP · max 5 MB
                        </span>
                      )}
                    </div>
                    {fieldErrors.template_image ? (
                      <p className="gff-error" role="alert">
                        {fieldErrors.template_image}
                      </p>
                    ) : null}
                  </div>
                </div>
            </GlobalFormShell>
          </div>

          <aside
            ref={previewPanelRef}
            className="cert-templates-editor__preview user-builder-panel"
          >
            {tablePreviewRow ? (
              <p className="cert-templates-preview-source">
                Viewing saved template: <strong>{tablePreviewRow.purpose_name}</strong>
                <GlobalButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cert-templates-preview-source__clear"
                  onClick={() => setTablePreviewRow(null)}
                >
                  Back to form preview
                </GlobalButton>
              </p>
            ) : null}
            <CertificateLivePreview
              imageUrl={previewImageUrl}
              purposeName={previewPurpose}
              recipientName={CERT_PREVIEW_SAMPLE_NAME}
              matter={previewMatter}
              nameFontFamily={previewNameFont}
              nameItalic={previewNameItalic}
              dateAlign={previewDateAlign}
            />
          </aside>
          </div>

          <div className="user-builder-panel cert-templates-list-panel">
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Saved templates</h3>
              <p className="user-builder-panel__hint">
                Template id is used when generating certificates from other modules.
              </p>
            </div>

            {loadError ? (
              <p className="ub-error" role="alert">
                {loadError}
              </p>
            ) : null}

            <GlobalDataTable
              wrapClassName="ub-table-wrap"
              tableClassName="ub-table"
              tableAriaLabel="Certificate templates"
              headers={[
                { key: "purpose_name", label: "Purpose" },
                { key: "purpose_slug", label: "Slug" },
                { key: "name_font_family", label: "Name font" },
                { key: "date_align", label: "Date" },
                { key: "attachment", label: "Attachment" },
                { key: "actions", label: "Actions", className: "ub-table__col-actions" },
              ]}
            >
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "1.5rem", color: "#64748b" }}>
                    No certificate templates yet. Add one above.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      tablePreviewRow?.id === row.id ? "cert-templates-row--previewing" : undefined
                    }
                  >
                    <td style={{ fontWeight: 600 }}>{row.purpose_name}</td>
                    <td>{row.purpose_slug}</td>
                    <td>
                      {FONT_OPTIONS.find(
                        (f) => f.value === normalizeFontFamilyId(row.name_font_family),
                      )?.label ||
                        normalizeFontFamilyId(row.name_font_family)}
                    </td>
                    <td style={{ textTransform: "capitalize" }}>{row.date_align || "center"}</td>
                    <td>
                      <TemplateAttachmentCell
                        templateId={row.template_image_url ? row.id : null}
                        onError={setFormError}
                      />
                    </td>
                    <td className="ub-table__col-actions">
                      <div className="ub-table-actions cert-templates-actions">
                        <GlobalTableActionButton
                          action="preview"
                          className="cert-templates-action-btn"
                          title="Preview template"
                          onClick={() => openTablePreview(row)}
                        />
                        <GlobalTableActionButton
                          action="edit"
                          className="cert-templates-action-btn"
                          title="Edit template"
                          onClick={() => startEdit(row)}
                        />
                        <GlobalTableActionButton
                          action="deactivate"
                          className="cert-templates-action-btn"
                          title="Deactivate template"
                          onClick={() => {
                            setItemToDelete(row);
                            setShowDelete(true);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </GlobalDataTable>
          </div>
      </div>

      <GlobalModal
        open={showDelete}
        onClose={() => !isDeleting && setShowDelete(false)}
        title="Deactivate certificate template"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton
              type="button"
              variant="ghost"
              disabled={isDeleting}
              onClick={() => setShowDelete(false)}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              disabled={isDeleting}
              onClick={confirmDelete}
            >
              {isDeleting ? "Deactivating…" : "Deactivate"}
            </GlobalButton>
          </div>
        }
      >
        <p>
          Deactivate <strong>{itemToDelete?.purpose_name}</strong>? Existing generate flows
          should stop using this template until you restore it by creating the same slug again.
        </p>
      </GlobalModal>
    </div>
  );
}
